import { useState, useEffect, useRef, useCallback } from "react";
import * as THREE from "three";
import * as d3 from "d3";
import { feature } from "topojson-client";

// Google Sheets Apps Script URL — replace with your deployed script URL
const SHEET_URL = "https://script.google.com/macros/s/AKfycbwQmIDF-s67OipkbYVDPgDTYNbP0F604uMQJVgKZb9I41RP8rx-TjxbOQPxziuDw_e8/exec";

// ─── PALETTE ───
const C = {
  night: "#0D1117", deep: "#161B22", surface: "#1C2128",
  border: "#30363D", muted: "#8B949E", text: "#C9D1D9",
  bright: "#E6EDF3", accent: "#D4A574",
  accentGlow: "rgba(212,165,116,0.15)", pulse: "#4A9EBF",
  pulseGlow: "rgba(74,158,191,0.12)", red: "#E06050",
  orange: "#D4A574", gray: "#8B949E", teal: "#6AB4A0", blue: "#4A9EBF",
};

// ─── PULSE RING ───
const PulseRing = ({ delay = 0, size = 300 }) => (
  <div style={{
    position: "absolute", width: size, height: size, borderRadius: "50%",
    border: "1px solid rgba(74,158,191,0.08)",
    animation: `pulseExpand 6s ease-out ${delay}s infinite`, pointerEvents: "none",
  }} />
);

const Section = ({ children, style = {}, ...rest }) => (
  <section {...rest} style={{ padding: "80px 24px", maxWidth: 720, margin: "0 auto", ...style }}>{children}</section>
);
const Divider = () => <div style={{ width: 60, height: 1, background: C.border, margin: "0 auto" }} />;

// ─── 3D GLOBE ───
const Globe = ({ ageGroup }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef({});
  const mouseRef = useRef({ isDown: false, prevX: 0, prevY: 0, velX: 0.003, downX: 0, downY: 0, dragDist: 0, hoveredCityIdx: -1 });
  const [selectedCity, setSelectedCity] = useState(null);
  const ageGroupRef = useRef(ageGroup);
  useEffect(() => { ageGroupRef.current = ageGroup; }, [ageGroup]);

  const cityData = [
    // ─── United States ───
    { lat: 40.71, lng: -74.01, name: "New York, NY", support: { all: 0.28, a18: 0.12, a30: 0.22, a45: 0.35, a60: 0.52 } },
    { lat: 34.05, lng: -118.24, name: "Los Angeles, CA", support: { all: 0.25, a18: 0.10, a30: 0.20, a45: 0.32, a60: 0.48 } },
    { lat: 41.88, lng: -87.63, name: "Chicago, IL", support: { all: 0.30, a18: 0.15, a30: 0.24, a45: 0.38, a60: 0.50 } },
    { lat: 29.76, lng: -95.37, name: "Houston, TX", support: { all: 0.45, a18: 0.25, a30: 0.38, a45: 0.52, a60: 0.65 } },
    { lat: 25.76, lng: -80.19, name: "Miami, FL", support: { all: 0.40, a18: 0.20, a30: 0.34, a45: 0.48, a60: 0.58 } },
    { lat: 33.45, lng: -112.07, name: "Phoenix, AZ", support: { all: 0.42, a18: 0.22, a30: 0.36, a45: 0.48, a60: 0.60 } },
    { lat: 39.95, lng: -75.17, name: "Philadelphia, PA", support: { all: 0.32, a18: 0.15, a30: 0.26, a45: 0.40, a60: 0.50 } },
    { lat: 29.42, lng: -98.49, name: "San Antonio, TX", support: { all: 0.44, a18: 0.24, a30: 0.38, a45: 0.50, a60: 0.62 } },
    { lat: 32.72, lng: -117.16, name: "San Diego, CA", support: { all: 0.34, a18: 0.16, a30: 0.28, a45: 0.40, a60: 0.52 } },
    { lat: 32.78, lng: -96.80, name: "Dallas, TX", support: { all: 0.46, a18: 0.26, a30: 0.40, a45: 0.52, a60: 0.64 } },
    { lat: 37.34, lng: -121.89, name: "San Jose, CA", support: { all: 0.22, a18: 0.08, a30: 0.16, a45: 0.28, a60: 0.42 } },
    { lat: 30.27, lng: -97.74, name: "Austin, TX", support: { all: 0.28, a18: 0.14, a30: 0.22, a45: 0.34, a60: 0.48 } },
    { lat: 30.33, lng: -81.66, name: "Jacksonville, FL", support: { all: 0.48, a18: 0.28, a30: 0.42, a45: 0.54, a60: 0.66 } },
    { lat: 39.96, lng: -83.00, name: "Columbus, OH", support: { all: 0.38, a18: 0.20, a30: 0.32, a45: 0.44, a60: 0.56 } },
    { lat: 35.23, lng: -80.84, name: "Charlotte, NC", support: { all: 0.40, a18: 0.22, a30: 0.34, a45: 0.46, a60: 0.58 } },
    { lat: 39.77, lng: -86.16, name: "Indianapolis, IN", support: { all: 0.44, a18: 0.24, a30: 0.38, a45: 0.50, a60: 0.60 } },
    { lat: 37.77, lng: -122.42, name: "San Francisco, CA", support: { all: 0.18, a18: 0.06, a30: 0.12, a45: 0.24, a60: 0.38 } },
    { lat: 39.74, lng: -104.99, name: "Denver, CO", support: { all: 0.30, a18: 0.14, a30: 0.24, a45: 0.36, a60: 0.48 } },
    { lat: 47.61, lng: -122.33, name: "Seattle, WA", support: { all: 0.22, a18: 0.08, a30: 0.16, a45: 0.28, a60: 0.42 } },
    { lat: 36.16, lng: -86.78, name: "Nashville, TN", support: { all: 0.44, a18: 0.24, a30: 0.38, a45: 0.50, a60: 0.62 } },
    { lat: 35.47, lng: -97.52, name: "Oklahoma City, OK", support: { all: 0.52, a18: 0.30, a30: 0.44, a45: 0.58, a60: 0.68 } },
    { lat: 42.36, lng: -71.06, name: "Boston, MA", support: { all: 0.24, a18: 0.10, a30: 0.18, a45: 0.30, a60: 0.44 } },
    { lat: 36.17, lng: -115.14, name: "Las Vegas, NV", support: { all: 0.38, a18: 0.20, a30: 0.32, a45: 0.44, a60: 0.54 } },
    { lat: 42.33, lng: -83.05, name: "Detroit, MI", support: { all: 0.36, a18: 0.18, a30: 0.30, a45: 0.42, a60: 0.52 } },
    { lat: 35.15, lng: -90.05, name: "Memphis, TN", support: { all: 0.42, a18: 0.22, a30: 0.36, a45: 0.48, a60: 0.58 } },
    { lat: 45.52, lng: -122.68, name: "Portland, OR", support: { all: 0.20, a18: 0.08, a30: 0.14, a45: 0.26, a60: 0.40 } },
    { lat: 39.29, lng: -76.61, name: "Baltimore, MD", support: { all: 0.32, a18: 0.16, a30: 0.26, a45: 0.38, a60: 0.48 } },
    { lat: 33.75, lng: -84.39, name: "Atlanta, GA", support: { all: 0.34, a18: 0.18, a30: 0.28, a45: 0.40, a60: 0.50 } },
    { lat: 44.98, lng: -93.27, name: "Minneapolis, MN", support: { all: 0.26, a18: 0.12, a30: 0.20, a45: 0.32, a60: 0.44 } },
    { lat: 29.95, lng: -90.07, name: "New Orleans, LA", support: { all: 0.38, a18: 0.20, a30: 0.32, a45: 0.44, a60: 0.54 } },
    { lat: 40.76, lng: -111.89, name: "Salt Lake City, UT", support: { all: 0.46, a18: 0.26, a30: 0.40, a45: 0.52, a60: 0.62 } },
    { lat: 40.44, lng: -79.99, name: "Pittsburgh, PA", support: { all: 0.38, a18: 0.20, a30: 0.32, a45: 0.44, a60: 0.54 } },
    { lat: 38.63, lng: -90.20, name: "St. Louis, MO", support: { all: 0.42, a18: 0.22, a30: 0.36, a45: 0.48, a60: 0.58 } },
    { lat: 38.91, lng: -77.04, name: "Washington, DC", support: { all: 0.26, a18: 0.12, a30: 0.20, a45: 0.32, a60: 0.44 } },
    { lat: 27.95, lng: -82.46, name: "Tampa, FL", support: { all: 0.42, a18: 0.22, a30: 0.36, a45: 0.48, a60: 0.58 } },
    { lat: 41.50, lng: -81.69, name: "Cleveland, OH", support: { all: 0.38, a18: 0.20, a30: 0.32, a45: 0.44, a60: 0.54 } },
    { lat: 61.22, lng: -149.90, name: "Anchorage, AK", support: { all: 0.48, a18: 0.26, a30: 0.42, a45: 0.54, a60: 0.64 } },
    { lat: 21.31, lng: -157.86, name: "Honolulu, HI", support: { all: 0.26, a18: 0.12, a30: 0.20, a45: 0.32, a60: 0.44 } },

    // ─── International ───
    { lat: 49.28, lng: -123.12, name: "Vancouver", support: { all: 0.18, a18: 0.06, a30: 0.12, a45: 0.22, a60: 0.35 } },
    { lat: 43.65, lng: -79.38, name: "Toronto", support: { all: 0.20, a18: 0.08, a30: 0.14, a45: 0.24, a60: 0.36 } },
    { lat: 45.50, lng: -73.57, name: "Montréal", support: { all: 0.16, a18: 0.06, a30: 0.12, a45: 0.20, a60: 0.32 } },
    { lat: 19.43, lng: -99.13, name: "Mexico City", support: { all: 0.12, a18: 0.05, a30: 0.08, a45: 0.15, a60: 0.22 } },
    { lat: 51.51, lng: -0.13, name: "London", support: { all: 0.32, a18: 0.15, a30: 0.26, a45: 0.40, a60: 0.55 } },
    { lat: 48.86, lng: 2.35, name: "Paris", support: { all: 0.24, a18: 0.10, a30: 0.18, a45: 0.30, a60: 0.45 } },
    { lat: 52.52, lng: 13.41, name: "Berlin", support: { all: 0.15, a18: 0.06, a30: 0.10, a45: 0.18, a60: 0.30 } },
    { lat: 40.42, lng: -3.70, name: "Madrid", support: { all: 0.20, a18: 0.08, a30: 0.16, a45: 0.26, a60: 0.36 } },
    { lat: 41.90, lng: 12.50, name: "Rome", support: { all: 0.22, a18: 0.10, a30: 0.18, a45: 0.28, a60: 0.38 } },
    { lat: 59.33, lng: 18.07, name: "Stockholm", support: { all: 0.18, a18: 0.08, a30: 0.14, a45: 0.22, a60: 0.32 } },
    { lat: 52.37, lng: 4.90, name: "Amsterdam", support: { all: 0.20, a18: 0.08, a30: 0.14, a45: 0.24, a60: 0.36 } },
    { lat: 53.35, lng: -6.26, name: "Dublin", support: { all: 0.22, a18: 0.10, a30: 0.16, a45: 0.28, a60: 0.38 } },
    { lat: 35.68, lng: 139.69, name: "Tokyo", support: { all: 0.20, a18: 0.10, a30: 0.16, a45: 0.24, a60: 0.35 } },
    { lat: 37.57, lng: 126.98, name: "Seoul", support: { all: 0.35, a18: 0.22, a30: 0.30, a45: 0.42, a60: 0.50 } },
    { lat: 22.32, lng: 114.17, name: "Hong Kong", support: { all: 0.28, a18: 0.15, a30: 0.22, a45: 0.34, a60: 0.42 } },
    { lat: 1.35, lng: 103.82, name: "Singapore", support: { all: 0.30, a18: 0.18, a30: 0.25, a45: 0.36, a60: 0.45 } },
    { lat: 28.61, lng: 77.21, name: "New Delhi", support: { all: 0.42, a18: 0.30, a30: 0.38, a45: 0.48, a60: 0.55 } },
    { lat: 19.08, lng: 72.88, name: "Mumbai", support: { all: 0.38, a18: 0.26, a30: 0.34, a45: 0.44, a60: 0.52 } },
    { lat: 25.20, lng: 55.27, name: "Dubai", support: { all: 0.15, a18: 0.06, a30: 0.10, a45: 0.18, a60: 0.28 } },
    { lat: 35.69, lng: 51.39, name: "Tehran", support: { all: 0.05, a18: 0.02, a30: 0.04, a45: 0.06, a60: 0.10 } },
    { lat: 31.23, lng: 121.47, name: "Shanghai", support: { all: 0.18, a18: 0.10, a30: 0.14, a45: 0.22, a60: 0.28 } },
    { lat: 32.08, lng: 34.78, name: "Tel Aviv", support: { all: 0.55, a18: 0.40, a30: 0.50, a45: 0.60, a60: 0.68 } },
    { lat: -23.55, lng: -46.63, name: "São Paulo", support: { all: 0.20, a18: 0.08, a30: 0.15, a45: 0.25, a60: 0.35 } },
    { lat: -34.60, lng: -58.38, name: "Buenos Aires", support: { all: 0.15, a18: 0.06, a30: 0.10, a45: 0.18, a60: 0.28 } },
    { lat: 4.71, lng: -74.07, name: "Bogotá", support: { all: 0.14, a18: 0.06, a30: 0.10, a45: 0.18, a60: 0.25 } },
    { lat: -12.05, lng: -77.04, name: "Lima", support: { all: 0.16, a18: 0.06, a30: 0.12, a45: 0.20, a60: 0.28 } },
    { lat: -33.93, lng: 18.42, name: "Cape Town", support: { all: 0.12, a18: 0.05, a30: 0.08, a45: 0.15, a60: 0.22 } },
    { lat: 30.04, lng: 31.24, name: "Cairo", support: { all: 0.08, a18: 0.03, a30: 0.06, a45: 0.10, a60: 0.15 } },
    { lat: 6.52, lng: 3.38, name: "Lagos", support: { all: 0.22, a18: 0.12, a30: 0.18, a45: 0.28, a60: 0.35 } },
    { lat: -1.29, lng: 36.82, name: "Nairobi", support: { all: 0.18, a18: 0.08, a30: 0.14, a45: 0.22, a60: 0.30 } },
    { lat: -33.87, lng: 151.21, name: "Sydney", support: { all: 0.30, a18: 0.15, a30: 0.24, a45: 0.38, a60: 0.50 } },
    { lat: -37.81, lng: 144.96, name: "Melbourne", support: { all: 0.26, a18: 0.12, a30: 0.20, a45: 0.34, a60: 0.46 } },
    { lat: -36.85, lng: 174.76, name: "Auckland", support: { all: 0.22, a18: 0.10, a30: 0.16, a45: 0.28, a60: 0.40 } },
    { lat: 55.75, lng: 37.62, name: "Moscow", support: { all: 0.08, a18: 0.04, a30: 0.06, a45: 0.10, a60: 0.14 } },
    { lat: 39.90, lng: 116.40, name: "Beijing", support: { all: 0.10, a18: 0.05, a30: 0.08, a45: 0.12, a60: 0.18 } },
    { lat: 13.76, lng: 100.50, name: "Bangkok", support: { all: 0.16, a18: 0.08, a30: 0.12, a45: 0.20, a60: 0.28 } },
    { lat: 14.60, lng: 120.98, name: "Manila", support: { all: 0.28, a18: 0.14, a30: 0.22, a45: 0.34, a60: 0.42 } },
    { lat: -6.21, lng: 106.85, name: "Jakarta", support: { all: 0.18, a18: 0.08, a30: 0.14, a45: 0.22, a60: 0.30 } },
    { lat: 41.01, lng: 28.98, name: "Istanbul", support: { all: 0.14, a18: 0.06, a30: 0.10, a45: 0.18, a60: 0.24 } },
  ];

  // Color from support value: 0 = red (oppose), 0.5 = neutral, 1.0 = blue (support)
  const supportToColor = (val) => {
    if (val < 0.5) {
      // Red to gray
      const t = val / 0.5;
      const r = Math.round(224 - (224 - 139) * t);
      const g = Math.round(96 + (149 - 96) * t);
      const b = Math.round(80 + (158 - 80) * t);
      return { r, g, b };
    } else {
      // Gray to blue
      const t = (val - 0.5) / 0.5;
      const r = Math.round(139 - (139 - 74) * t);
      const g = Math.round(149 + (158 - 149) * t);
      const b = Math.round(158 + (191 - 158) * t);
      return { r, g, b };
    }
  };

  // Generate world map texture on canvas
  const createWorldTexture = (geoData) => {
    const canvas = document.createElement("canvas");
    const W = 2048, H = 1024;
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    // Ocean
    ctx.fillStyle = "#0D1117";
    ctx.fillRect(0, 0, W, H);

    // d3 equirectangular projection mapped to canvas
    const projection = d3.geoEquirectangular()
      .scale(W / (2 * Math.PI))
      .translate([W / 2, H / 2]);
    const path = d3.geoPath(projection, ctx);

    // Draw countries
    if (geoData && geoData.features) {
      geoData.features.forEach(feature => {
        ctx.beginPath();
        path(feature);
        ctx.fillStyle = "rgba(74, 158, 191, 0.1)";
        ctx.fill();
        ctx.strokeStyle = "rgba(74, 158, 191, 0.25)";
        ctx.lineWidth = 0.8;
        ctx.stroke();
      });
    }

    // Subtle grid
    ctx.strokeStyle = "rgba(74, 158, 191, 0.04)";
    ctx.lineWidth = 0.5;
    for (let lat = -60; lat <= 60; lat += 30) {
      const y = projection([0, lat])[1];
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
    }
    for (let lng = -150; lng <= 180; lng += 30) {
      const x = projection([lng, 0])[0];
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
    }

    return canvas;
  };

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;

    const init = async () => {
      // Fetch real GeoJSON country data
      let geoData = null;
      try {
        const res = await fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json");
        const topo = await res.json();
        geoData = feature(topo, topo.objects.countries);
      } catch (e) {
        console.warn("Could not load world atlas, using blank globe");
      }

      if (cancelled) return;

    const w = mount.clientWidth;
    const h = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 1000);
    camera.position.z = 3.2;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(w, h);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // World map texture
    const worldCanvas = createWorldTexture(geoData);
    const worldTexture = new THREE.CanvasTexture(worldCanvas);
    worldTexture.wrapS = THREE.RepeatWrapping;

    // Globe with texture
    const globeGeo = new THREE.SphereGeometry(1, 64, 64);
    const globeMat = new THREE.MeshBasicMaterial({
      map: worldTexture,
      transparent: false,
    });
    const globe = new THREE.Mesh(globeGeo, globeMat);
    globe.rotation.x = 0.25; // Slight tilt to show northern hemisphere better
    scene.add(globe);

    // Atmosphere glow
    const atmosGeo = new THREE.SphereGeometry(1.03, 64, 64);
    const atmosMat = new THREE.MeshBasicMaterial({
      color: 0x4A9EBF, transparent: true, opacity: 0.04, side: THREE.BackSide,
    });
    const atmos = new THREE.Mesh(atmosGeo, atmosMat);
    scene.add(atmos);

    // Data points
    const pointsGroup = new THREE.Group();
    scene.add(pointsGroup);

    const createPoints = (age) => {
      while (pointsGroup.children.length) pointsGroup.remove(pointsGroup.children[0]);

      cityData.forEach((city, idx) => {
        const phi = (90 - city.lat) * (Math.PI / 180);
        const theta = (city.lng + 180) * (Math.PI / 180);
        const support = city.support[age];
        const col = supportToColor(support);

        const x = -1.01 * Math.sin(phi) * Math.cos(theta);
        const y = 1.01 * Math.cos(phi);
        const z = 1.01 * Math.sin(phi) * Math.sin(theta);

        // Point dot — size scales with how strong the opinion is (far from 0.5)
        const conviction = Math.abs(support - 0.5) * 2; // 0 = split, 1 = unanimous
        const dotSize = 0.018 + conviction * 0.022;
        const dotGeo = new THREE.SphereGeometry(dotSize, 8, 8);
        const dotMat = new THREE.MeshBasicMaterial({
          color: new THREE.Color(`rgb(${col.r},${col.g},${col.b})`),
          transparent: true,
          opacity: 0.7 + conviction * 0.3,
        });
        const dot = new THREE.Mesh(dotGeo, dotMat);
        dot.position.set(x, y, z);
        dot.userData = { baseScale: 1, pulseOffset: idx * 0.5, city, cityIdx: idx };
        pointsGroup.add(dot);

        // Glow ring
        const ringGeo = new THREE.RingGeometry(0.025, 0.05 + conviction * 0.02, 16);
        const ringMat2 = new THREE.MeshBasicMaterial({
          color: new THREE.Color(`rgb(${col.r},${col.g},${col.b})`),
          transparent: true, opacity: 0.12 + conviction * 0.18, side: THREE.DoubleSide,
        });
        const ring = new THREE.Mesh(ringGeo, ringMat2);
        ring.position.set(x, y, z);
        ring.lookAt(0, 0, 0);
        ring.userData = { baseScale: 1, pulseOffset: idx * 0.5, isRing: true, cityIdx: idx };
        pointsGroup.add(ring);
      });
    };

    createPoints("all");
    sceneRef.current = { scene, camera, renderer, globe, atmos, pointsGroup, createPoints };

    // Animation
    let frameId;
    const clock = new THREE.Clock();
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const t = clock.getElapsedTime();
      const m = mouseRef.current;

      // Only auto-spin when at default zoom or further out
      const isZoomedOut = camera.position.z >= 3.1;
      const shouldSpin = !m.isDown && isZoomedOut;
      globe.rotation.y += shouldSpin ? m.velX : 0;
      atmos.rotation.y = globe.rotation.y;
      atmos.rotation.x = globe.rotation.x;
      pointsGroup.rotation.y = globe.rotation.y;
      pointsGroup.rotation.x = globe.rotation.x;

      pointsGroup.children.forEach(child => {
        if (child.userData.pulseOffset !== undefined) {
          const pulse = 1 + Math.sin(t * 1.4 + child.userData.pulseOffset) * 0.12;
          const isHovered = child.userData.cityIdx === m.hoveredCityIdx;
          const hoverBoost = isHovered ? 1.9 : 1;
          child.scale.setScalar(pulse * hoverBoost);
        }
      });

      renderer.render(scene, camera);
    };
    animate();

    // Raycaster for hover + click picking
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const pickDotAtClient = (clientX, clientY) => {
      const rect = el.getBoundingClientRect();
      ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const dots = pointsGroup.children.filter(c => !c.userData.isRing);
      const hits = raycaster.intersectObjects(dots, false);
      return hits.length > 0 ? hits[0].object.userData : null;
    };

    // Mouse / touch interaction
    const onDown = (e) => {
      e.preventDefault();
      m.isDown = true;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      m.prevX = clientX;
      m.prevY = clientY;
      m.downX = clientX;
      m.downY = clientY;
      m.dragDist = 0;
    };
    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;

      if (m.isDown) {
        e.preventDefault();
        const dx = (clientX - m.prevX) * 0.005;
        const dy = (clientY - m.prevY) * 0.005;
        globe.rotation.y += dx;
        globe.rotation.x = Math.max(-0.78, Math.min(0.78, globe.rotation.x + dy));
        atmos.rotation.y = globe.rotation.y;
        atmos.rotation.x = globe.rotation.x;
        pointsGroup.rotation.y = globe.rotation.y;
        pointsGroup.rotation.x = globe.rotation.x;
        m.prevX = clientX;
        m.prevY = clientY;
        m.dragDist += Math.abs(clientX - m.prevX) + Math.abs(clientY - m.prevY);
      } else {
        // Hover detection only when not dragging
        const hit = pickDotAtClient(clientX, clientY);
        const newIdx = hit ? hit.cityIdx : -1;
        if (newIdx !== m.hoveredCityIdx) {
          m.hoveredCityIdx = newIdx;
          el.style.cursor = newIdx >= 0 ? "pointer" : "grab";
        }
      }
    };
    const onUp = (e) => {
      if (m.isDown) {
        const totalDrag = Math.abs((e && (e.clientX ?? 0)) - m.downX) + Math.abs((e && (e.clientY ?? 0)) - m.downY);
        // If barely moved, treat as click — pick city and open popup
        if (totalDrag < 6 && e && e.clientX !== undefined) {
          const hit = pickDotAtClient(e.clientX, e.clientY);
          if (hit && hit.city) {
            setSelectedCity(hit.city);
          }
        }
      }
      m.isDown = false;
    };

    // Zoom via scroll wheel (extended zoom-in range)
    const onWheel = (e) => {
      e.preventDefault();
      const zoomSpeed = 0.001;
      camera.position.z = Math.max(1.25, Math.min(5, camera.position.z + e.deltaY * zoomSpeed));
    };

    // Zoom via pinch on mobile
    let lastPinchDist = 0;
    const getPinchDist = (e) => {
      if (e.touches.length < 2) return 0;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };
    const onTouchStart = (e) => {
      e.preventDefault();
      if (e.touches.length === 2) {
        lastPinchDist = getPinchDist(e);
      } else {
        onDown(e);
      }
    };
    const onTouchMove = (e) => {
      e.preventDefault();
      if (e.touches.length === 2) {
        const dist = getPinchDist(e);
        if (lastPinchDist > 0) {
          const delta = (lastPinchDist - dist) * 0.01;
          camera.position.z = Math.max(1.25, Math.min(5, camera.position.z + delta));
        }
        lastPinchDist = dist;
      } else {
        onMove(e);
      }
    };
    const onTouchEnd = (e) => {
      lastPinchDist = 0;
      // If it was a short tap (not a drag), treat like a click
      if (m.isDown && e.changedTouches && e.changedTouches.length === 1) {
        const t0 = e.changedTouches[0];
        const totalDrag = Math.abs(t0.clientX - m.downX) + Math.abs(t0.clientY - m.downY);
        if (totalDrag < 10) {
          const hit = pickDotAtClient(t0.clientX, t0.clientY);
          if (hit && hit.city) setSelectedCity(hit.city);
        }
      }
      m.isDown = false;
    };

    const m = mouseRef.current;
    const el = renderer.domElement;
    el.style.cursor = "grab";
    const onLeave = (e) => {
      m.hoveredCityIdx = -1;
      el.style.cursor = "grab";
      onUp(e);
    };
    el.addEventListener("mousedown", onDown);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseup", onUp);
    el.addEventListener("mouseleave", onLeave);
    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: false });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);

    const cleanupFn = () => {
      cancelAnimationFrame(frameId);
      el.removeEventListener("mousedown", onDown);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseup", onUp);
      el.removeEventListener("mouseleave", onLeave);
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
    sceneRef.current.cleanup = cleanupFn;
    }; // end async init

    init();

    return () => {
      cancelled = true;
      if (sceneRef.current.cleanup) sceneRef.current.cleanup();
    };
  }, []);

  // Update points when age group changes
  useEffect(() => {
    if (sceneRef.current.createPoints) {
      sceneRef.current.createPoints(ageGroup);
    }
  }, [ageGroup]);

  const ageLabels = { all: "All ages", a18: "18–29", a30: "30–44", a45: "45–59", a60: "60+" };
  const ageKeys = ["a18", "a30", "a45", "a60"];

  return (
    <div>
      <div ref={mountRef} style={{
        width: "100%", height: 320, cursor: "grab", borderRadius: 8,
        position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)",
          fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.muted,
          letterSpacing: 1, pointerEvents: "none", opacity: 0.6,
        }}>drag to explore · scroll to zoom · click a city</div>

        {selectedCity && (
          <div style={{
            position: "absolute", top: 12, right: 12, width: 220,
            background: "rgba(13, 17, 23, 0.94)", border: `1px solid ${C.border}`,
            borderRadius: 6, padding: 14, backdropFilter: "blur(6px)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)", zIndex: 10,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
              <span style={{
                fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 14,
                color: C.bright, lineHeight: 1.3,
              }}>{selectedCity.name}</span>
              <button onClick={() => setSelectedCity(null)} style={{
                background: "transparent", border: "none", color: C.muted,
                cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0, marginLeft: 8,
              }} aria-label="Close">×</button>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{
                fontFamily: "'DM Sans', sans-serif", fontSize: 9, fontWeight: 600,
                letterSpacing: 1.5, textTransform: "uppercase", color: "#E06050",
              }}>Oppose</span>
              <span style={{
                fontFamily: "'DM Sans', sans-serif", fontSize: 9, fontWeight: 600,
                letterSpacing: 1.5, textTransform: "uppercase", color: "#4A9EBF",
              }}>Support</span>
            </div>
            {ageKeys.map(k => {
              const supportPct = Math.round(selectedCity.support[k] * 100);
              const opposePct = 100 - supportPct;
              return (
                <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.muted, width: 40, flexShrink: 0 }}>{ageLabels[k]}</span>
                  <div style={{ flex: 1, height: 8, borderRadius: 2, overflow: "hidden", display: "flex" }}>
                    <div style={{ width: `${opposePct}%`, height: "100%", background: "#E06050" }} />
                    <div style={{ width: `${supportPct}%`, height: "100%", background: "#4A9EBF" }} />
                  </div>
                  <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.muted, width: 52, textAlign: "right" }}>
                    <span style={{ color: "#E06050", fontWeight: 600 }}>{opposePct}</span>
                    <span style={{ opacity: 0.4 }}> / </span>
                    <span style={{ color: "#4A9EBF", fontWeight: 600 }}>{supportPct}</span>
                  </span>
                </div>
              );
            })}
            <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 10, paddingTop: 8 }}>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, marginBottom: 6 }}>
                Overall
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 }}>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#E06050" }}>Oppose</span>
                <span style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 15, color: "#E06050", fontWeight: 600 }}>
                  {100 - Math.round(selectedCity.support.all * 100)}%
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#4A9EBF" }}>Support</span>
                <span style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 15, color: "#4A9EBF", fontWeight: 600 }}>
                  {Math.round(selectedCity.support.all * 100)}%
                </span>
              </div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 9, color: C.muted, fontStyle: "italic", marginTop: 8 }}>
                Example data — illustrative only
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Color legend */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        gap: 8, marginTop: 12, opacity: 0.8,
      }}>
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: "#E06050", fontWeight: 600 }}>Oppose</span>
        <div style={{
          width: 120, height: 6, borderRadius: 3,
          background: "linear-gradient(to right, #E06050, #8B959E, #4A9EBF)",
        }} />
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: "#4A9EBF", fontWeight: 600 }}>Support</span>
      </div>
    </div>
  );
};

// ─── DEMOGRAPHIC FILTER DATA ───
const HERO_POLL_DEMO = {
  all: [34, 22, 8, 18, 18],
  age: { "18–29": [42, 24, 10, 14, 10], "30–44": [30, 20, 8, 22, 20], "45–59": [24, 18, 8, 24, 26], "60+": [38, 26, 10, 16, 10] },
  gender: { "Male": [30, 20, 8, 22, 20], "Female": [38, 24, 8, 16, 14], "Non-binary": [40, 24, 10, 14, 12] },
  location: {
    "California": [36, 24, 8, 18, 14], "Texas": [28, 20, 8, 22, 22],
    "New York": [38, 24, 8, 16, 14], "Florida": [32, 22, 10, 18, 18],
    "Pennsylvania": [34, 22, 10, 18, 16], "Illinois": [36, 24, 8, 18, 14],
    "Ohio": [38, 24, 10, 16, 12], "Georgia": [30, 20, 8, 22, 20],
    "North Carolina": [26, 20, 8, 22, 24], "Arizona": [30, 22, 8, 22, 18],
  },
};

const WEIGH_IN_DEMO = {
  all: [76, 12, 12],
  age: { "18–29": [89, 6, 5], "30–44": [82, 8, 10], "45–59": [71, 14, 15], "60+": [58, 24, 18] },
  gender: { "Male": [72, 16, 12], "Female": [80, 8, 12], "Non-binary": [85, 5, 10] },
  location: {
    "California": [82, 8, 10], "Texas": [68, 18, 14],
    "New York": [80, 10, 10], "Florida": [72, 15, 13],
    "Pennsylvania": [76, 12, 12], "Illinois": [78, 11, 11],
    "Ohio": [74, 14, 12], "Georgia": [72, 15, 13],
    "North Carolina": [70, 16, 14], "Arizona": [74, 14, 12],
  },
};

const TREND_DEMO = {
  all: [71, 66, 60, 56, 52, 46, 44, 42, 42, 45, 43, 44, 42, 42],
  age: {
    "18–29": [82, 78, 74, 70, 66, 60, 58, 56, 57, 62, 60, 61, 59, 58],
    "30–44": [73, 68, 62, 58, 54, 48, 46, 44, 44, 47, 45, 46, 44, 44],
    "45–59": [66, 61, 55, 51, 47, 41, 39, 37, 37, 39, 37, 38, 36, 36],
    "60+": [60, 55, 48, 44, 40, 34, 32, 30, 29, 31, 29, 30, 28, 27],
  },
  gender: {
    "Male": [68, 63, 57, 53, 49, 43, 41, 39, 39, 42, 40, 41, 39, 39],
    "Female": [74, 69, 63, 59, 55, 49, 47, 45, 45, 48, 46, 47, 45, 45],
    "Non-binary": [76, 71, 66, 62, 58, 52, 50, 48, 48, 52, 50, 51, 49, 48],
  },
  location: {
    "California": [72, 67, 61, 57, 53, 47, 45, 43, 44, 47, 45, 46, 44, 43],
    "Texas": [75, 70, 64, 60, 56, 50, 48, 46, 46, 49, 47, 48, 46, 46],
    "New York": [68, 63, 57, 53, 49, 43, 41, 39, 39, 42, 40, 41, 39, 39],
    "Florida": [73, 68, 62, 58, 54, 48, 46, 44, 45, 48, 46, 47, 45, 45],
    "Pennsylvania": [69, 64, 57, 53, 49, 42, 40, 38, 38, 41, 39, 40, 38, 38],
    "Illinois": [70, 65, 59, 55, 51, 45, 43, 41, 41, 44, 42, 43, 41, 41],
    "Ohio": [67, 62, 55, 51, 47, 41, 39, 37, 37, 40, 38, 39, 37, 37],
    "Georgia": [74, 69, 63, 59, 55, 49, 47, 45, 45, 48, 46, 47, 45, 45],
    "North Carolina": [75, 70, 64, 60, 56, 50, 48, 46, 46, 49, 47, 48, 46, 46],
    "Arizona": [73, 68, 62, 58, 54, 48, 46, 44, 44, 47, 45, 46, 44, 44],
  },
};

const DEMO_COLORS = {
  age: { "18–29": "#D4A574", "30–44": "#4A9EBF", "45–59": "#6AB4A0", "60+": "#E06050" },
  gender: { "Male": "#4A9EBF", "Female": "#D4A574", "Non-binary": "#6AB4A0" },
  location: {
    "California": "#4A9EBF", "Texas": "#D4A574", "New York": "#6AB4A0",
    "Florida": "#E06050", "Pennsylvania": "#4A9EBF", "Illinois": "#D4A574",
    "Ohio": "#6AB4A0", "Georgia": "#E06050", "North Carolina": "#4A9EBF",
    "Arizona": "#D4A574",
  },
};

const FILTER_SUBS = {
  age: ["18–29", "30–44", "45–59", "60+"],
  gender: ["Male", "Female", "Non-binary"],
  location: ["California", "Texas", "New York", "Florida", "Pennsylvania", "Illinois", "Ohio", "Georgia", "North Carolina", "Arizona"],
};

// Realistic response counts per filter/sub-filter. Age/gender buckets sum to the total;
// location buckets reflect responses from each state (subset of total, not exhaustive).
const HERO_POLL_COUNTS = {
  all: 24891,
  age: { "18–29": 6243, "30–44": 7012, "45–59": 5921, "60+": 5715 },
  gender: { "Male": 11928, "Female": 12198, "Non-binary": 765 },
  location: {
    "California": 3124, "Texas": 2456, "New York": 1987, "Florida": 2103,
    "Pennsylvania": 1445, "Illinois": 1382, "Ohio": 1256, "Georgia": 1198,
    "North Carolina": 1087, "Arizona": 892,
  },
};

const WEIGH_IN_COUNTS = {
  all: 41203,
  age: { "18–29": 10389, "30–44": 11632, "45–59": 9824, "60+": 9358 },
  gender: { "Male": 19756, "Female": 20184, "Non-binary": 1263 },
  location: {
    "California": 5167, "Texas": 4089, "New York": 3278, "Florida": 3492,
    "Pennsylvania": 2384, "Illinois": 2287, "Ohio": 2073, "Georgia": 1978,
    "North Carolina": 1795, "Arizona": 1468,
  },
};

const TREND_COUNTS = {
  all: 12847,
  age: { "18–29": 3247, "30–44": 3623, "45–59": 3056, "60+": 2921 },
  gender: { "Male": 6158, "Female": 6285, "Non-binary": 404 },
  location: {
    "California": 1612, "Texas": 1278, "New York": 1022, "Florida": 1087,
    "Pennsylvania": 744, "Illinois": 713, "Ohio": 648, "Georgia": 620,
    "North Carolina": 562, "Arizona": 462,
  },
};

const MANDATE_DEMO = {
  all: [68, 19, 13],
  age: { "18–29": [75, 16, 9], "30–44": [70, 18, 12], "45–59": [66, 20, 14], "60+": [62, 22, 16] },
  gender: { "Male": [64, 20, 16], "Female": [72, 18, 10], "Non-binary": [78, 15, 7] },
  location: {
    "California": [74, 18, 8], "Texas": [62, 18, 20], "New York": [76, 16, 8],
    "Florida": [65, 18, 17], "Pennsylvania": [68, 20, 12], "Illinois": [70, 18, 12],
    "Ohio": [65, 20, 15], "Georgia": [67, 18, 15], "North Carolina": [66, 18, 16], "Arizona": [67, 20, 13],
  },
};

const MANDATE_COUNTS = {
  all: 31847,
  age: { "18–29": 8012, "30–44": 8934, "45–59": 7623, "60+": 7278 },
  gender: { "Male": 15312, "Female": 15912, "Non-binary": 623 },
  location: {
    "California": 4123, "Texas": 3234, "New York": 2567, "Florida": 2789,
    "Pennsylvania": 1878, "Illinois": 1734, "Ohio": 1623, "Georgia": 1534,
    "North Carolina": 1389, "Arizona": 1167,
  },
};

const getCount = (counts, filter, subFilter) => {
  if (filter === "all") return counts.all;
  return (counts[filter] && counts[filter][subFilter]) || counts.all;
};

const FilterChips = ({ filter, setFilter, subFilter, setSubFilter }) => {
  const filters = ["all", "age", "gender", "location"];
  const labels = { all: "All", age: "Age", gender: "Gender", location: "Location" };
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 6, marginBottom: filter !== "all" ? 8 : 0 }}>
        {filters.map(f => (
          <button key={f} onClick={() => {
            setFilter(f);
            if (f === "all") setSubFilter(null);
            else setSubFilter(FILTER_SUBS[f][0]);
          }} style={{
            padding: "4px 10px", fontFamily: "'DM Sans', sans-serif", fontSize: 11,
            letterSpacing: 1, textTransform: "uppercase", whiteSpace: "nowrap",
            color: filter === f ? C.night : C.muted,
            background: filter === f ? C.accent : "transparent",
            border: `1px solid ${filter === f ? C.accent : C.border}`,
            borderRadius: 20, cursor: "pointer", transition: "all 0.3s",
          }}>{labels[f]}</button>
        ))}
      </div>
      {filter !== "all" && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {FILTER_SUBS[filter].map(sf => (
            <button key={sf} onClick={() => setSubFilter(sf)} style={{
              padding: "3px 8px", fontFamily: "'DM Sans', sans-serif", fontSize: 10,
              letterSpacing: 0.5, color: subFilter === sf ? C.night : C.muted,
              background: subFilter === sf ? C.accent : "transparent",
              border: `1px solid ${subFilter === sf ? C.accent : C.border}`,
              borderRadius: 16, cursor: "pointer", transition: "all 0.3s",
            }}>{sf}</button>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── HERO POLL ───
const HeroPoll = ({ onVote, hasVoted, voteValue }) => {
  const [value, setValue] = useState(50);
  // Apolitical single-hue ramp: dim bronze (scarcity) → bright gold (prosperity)
  const rampColors = ["#4A3D2A", "#7A6340", "#A88757", "#C79A66", "#D4A574"];
  const getColor = (v) => {
    if (v < 20) return rampColors[0];
    if (v < 40) return rampColors[1];
    if (v < 60) return rampColors[2];
    if (v < 80) return rampColors[3];
    return rampColors[4];
  };
  // Bimodal distribution: strong opinions cluster at the ends, not the middle
  const [filter, setFilter] = useState("all");
  const [subFilter, setSubFilter] = useState(null);
  const pcts = filter === "all" ? HERO_POLL_DEMO.all
    : (HERO_POLL_DEMO[filter] && HERO_POLL_DEMO[filter][subFilter]) || HERO_POLL_DEMO.all;
  const buckets = [
    { label: "0–20", pct: pcts[0], color: rampColors[0] },
    { label: "20–40", pct: pcts[1], color: rampColors[1] },
    { label: "40–60", pct: pcts[2], color: rampColors[2] },
    { label: "60–80", pct: pcts[3], color: rampColors[3] },
    { label: "80–100", pct: pcts[4], color: rampColors[4] },
  ];
  const [animW, setAnimW] = useState(0);
  useEffect(() => { if (hasVoted) { const t = setTimeout(() => setAnimW(100), 100); return () => clearTimeout(t); } }, [hasVoted]);
  const userBucket = hasVoted ? Math.min(Math.floor(voteValue / 20), 4) : -1;

  return (
    <div style={{ width: "100%", maxWidth: 500, margin: "0 auto", textAlign: "center" }}>
      <div style={{
        display: "inline-block", padding: "4px 10px", marginBottom: 16,
        border: `1px solid ${C.border}`, borderRadius: 3,
        fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 600,
        letterSpacing: 2, textTransform: "uppercase", color: C.muted,
      }}>Example</div>
      <p style={{
        fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 20,
        color: C.bright, textAlign: "center", lineHeight: 1.5, marginBottom: 32,
      }}>Are you better off financially than you were two years ago?</p>
      {!hasVoted ? (
        <>
          <div style={{ position: "relative", padding: "8px 0", marginBottom: 12 }}>
            <input type="range" min="0" max="100" value={value}
              onChange={(e) => setValue(Number(e.target.value))}
              style={{
                width: "100%", height: 6, appearance: "none", WebkitAppearance: "none",
                background: `linear-gradient(to right, ${rampColors[0]}, ${rampColors[1]}, ${rampColors[2]}, ${rampColors[3]}, ${rampColors[4]})`,
                borderRadius: 3, outline: "none", cursor: "pointer",
              }} />
            <style>{`
              input[type='range']::-webkit-slider-thumb {
                -webkit-appearance: none; width: 24px; height: 24px; border-radius: 50%;
                background: ${getColor(value)}; border: 3px solid ${C.bright};
                cursor: grab; box-shadow: 0 0 12px ${getColor(value)}66;
              }
              input[type='range']::-moz-range-thumb {
                width: 24px; height: 24px; border-radius: 50%;
                background: ${getColor(value)}; border: 3px solid ${C.bright}; cursor: grab;
              }
            `}</style>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 28 }}>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: C.muted }}>Much worse off</span>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: C.muted }}>Much better off</span>
          </div>
          <button onClick={() => onVote(value)} style={{
            display: "block", margin: "0 auto", padding: "12px 40px",
            fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600,
            letterSpacing: 1.5, textTransform: "uppercase", color: C.night,
            background: C.accent, border: "none", borderRadius: 4, cursor: "pointer", transition: "all 0.3s",
          }}
            onMouseEnter={(e) => { e.target.style.background = C.bright; e.target.style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { e.target.style.background = C.accent; e.target.style.transform = "translateY(0)"; }}
          >Submit</button>
        </>
      ) : (
        <div>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: C.muted, textAlign: "center", marginBottom: 20 }}>
            This is an example. Your input isn't recorded yet — but here's what the real thing will look like:
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {buckets.map((b, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: C.muted, width: 42, textAlign: "right", flexShrink: 0 }}>{b.label}</span>
                <div style={{ flex: 1, height: 24, background: C.deep, borderRadius: 3, overflow: "hidden", position: "relative" }}>
                  <div style={{
                    width: `${(animW / 100) * b.pct}%`, height: "100%", background: b.color,
                    borderRadius: 3, transition: "width 1s ease-out", opacity: i === userBucket ? 1 : 0.6,
                  }} />
                  {i === userBucket && (
                    <span style={{
                      position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                      fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.bright, fontWeight: 600,
                    }}>YOU</span>
                  )}
                </div>
                <span style={{
                  fontFamily: "'DM Sans', sans-serif", fontSize: 12, width: 32, flexShrink: 0,
                  color: i === userBucket ? C.bright : C.muted, fontWeight: i === userBucket ? 600 : 400,
                }}>{b.pct}%</span>
              </div>
            ))}
          </div>
          <FilterChips filter={filter} setFilter={setFilter} subFilter={subFilter} setSubFilter={setSubFilter} />
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.muted, marginTop: 10, textAlign: "center", fontStyle: "italic" }}>
            N = <span style={{ color: C.bright, fontWeight: 600, fontStyle: "normal" }}>{getCount(HERO_POLL_COUNTS, filter, subFilter).toLocaleString()}</span> verified responses
          </p>
        </div>
      )}
    </div>
  );
};

// ─── GLOBE VOTE PROMPT ───
const GlobeVotePrompt = () => {
  const [vote, setVote] = useState(null); // null | "oppose" | "support"

  // Example comparison stats (illustrative, in line with the globe data)
  const stats = {
    oppose: { world: 75, us: 60, byAge: { "18–29": 86, "30–44": 72, "45–59": 56, "60+": 44 } },
    support: { world: 25, us: 40, byAge: { "18–29": 14, "30–44": 28, "45–59": 44, "60+": 56 } },
  };

  if (!vote) {
    return (
      <div style={{
        marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.border}`,
        textAlign: "center",
      }}>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
          letterSpacing: 2, textTransform: "uppercase", color: C.muted, marginBottom: 12,
        }}>Where do you stand?</p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <button onClick={() => setVote("oppose")} style={{
            padding: "10px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
            letterSpacing: 1.5, textTransform: "uppercase", color: "#E06050",
            background: "transparent", border: `1px solid #E06050`, borderRadius: 4, cursor: "pointer",
            transition: "all 0.2s",
          }}
            onMouseEnter={(e) => { e.target.style.background = "#E06050"; e.target.style.color = C.night; }}
            onMouseLeave={(e) => { e.target.style.background = "transparent"; e.target.style.color = "#E06050"; }}
          >Oppose</button>
          <button onClick={() => setVote("support")} style={{
            padding: "10px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
            letterSpacing: 1.5, textTransform: "uppercase", color: "#4A9EBF",
            background: "transparent", border: `1px solid #4A9EBF`, borderRadius: 4, cursor: "pointer",
            transition: "all 0.2s",
          }}
            onMouseEnter={(e) => { e.target.style.background = "#4A9EBF"; e.target.style.color = C.night; }}
            onMouseLeave={(e) => { e.target.style.background = "transparent"; e.target.style.color = "#4A9EBF"; }}
          >Support</button>
        </div>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.muted,
          fontStyle: "italic", marginTop: 4,
        }}>Example — your input isn't recorded</p>
      </div>
    );
  }

  const s = stats[vote];
  const voteColor = vote === "oppose" ? "#E06050" : "#4A9EBF";
  const voteLabel = vote === "oppose" ? "Oppose" : "Support";

  return (
    <div style={{
      marginTop: 20, paddingTop: 20, borderTop: `1px solid ${C.border}`,
      animation: "fadeIn 0.4s ease-out",
    }}>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: C.muted, marginBottom: 4, letterSpacing: 1 }}>
          You voted
        </p>
        <p style={{
          fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 22,
          color: voteColor, fontWeight: 600,
        }}>{voteLabel}</p>
      </div>

      <div style={{
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16,
      }}>
        <div style={{ padding: "12px 14px", background: C.deep, borderRadius: 4, textAlign: "center" }}>
          <div style={{
            fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 26,
            color: voteColor, fontWeight: 600, lineHeight: 1,
          }}>{s.world}%</div>
          <div style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.muted,
            marginTop: 6, letterSpacing: 1, textTransform: "uppercase",
          }}>of the world<br/>agrees with you</div>
        </div>
        <div style={{ padding: "12px 14px", background: C.deep, borderRadius: 4, textAlign: "center" }}>
          <div style={{
            fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 26,
            color: voteColor, fontWeight: 600, lineHeight: 1,
          }}>{s.us}%</div>
          <div style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.muted,
            marginTop: 6, letterSpacing: 1, textTransform: "uppercase",
          }}>of Americans<br/>agree with you</div>
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 10, fontWeight: 600,
          letterSpacing: 1.5, textTransform: "uppercase", color: C.muted, marginBottom: 8, textAlign: "center",
        }}>Across generations</p>
        <div style={{ display: "flex", gap: 6 }}>
          {Object.entries(s.byAge).map(([label, pct]) => (
            <div key={label} style={{ flex: 1, textAlign: "center" }}>
              <div style={{ height: 60, display: "flex", alignItems: "flex-end", justifyContent: "center", marginBottom: 4 }}>
                <div style={{
                  width: "60%", height: `${pct}%`,
                  background: voteColor, opacity: 0.85, borderRadius: "2px 2px 0 0",
                }} />
              </div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.bright, fontWeight: 600 }}>{pct}%</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 9, color: C.muted, marginTop: 1 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      <p style={{
        fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 12,
        color: C.accent, textAlign: "center", fontStyle: "italic", marginBottom: 10,
      }}>
        These are example numbers. Real Populi requires verified identity — <a href="#signup" style={{ color: C.accent, textDecoration: "underline" }}>join the waitlist</a> to be counted when we launch.
      </p>

      <div style={{ textAlign: "center" }}>
        <button onClick={() => setVote(null)} style={{
          padding: "6px 14px", fontFamily: "'DM Sans', sans-serif", fontSize: 11,
          color: C.muted, background: "transparent", border: `1px solid ${C.border}`,
          borderRadius: 3, cursor: "pointer",
        }}>Change my answer</button>
      </div>
    </div>
  );
};

// ─── DEMO: WEIGH IN ───
const DemoWeighIn = () => {
  const [selected, setSelected] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [filter, setFilter] = useState("all");
  const [subFilter, setSubFilter] = useState(null);
  const results = filter === "all" ? WEIGH_IN_DEMO.all
    : (WEIGH_IN_DEMO[filter] && WEIGH_IN_DEMO[filter][subFilter]) || WEIGH_IN_DEMO.all;
  const options = [
    { label: "Yes, ban it completely", color: C.blue },
    { label: "No, it's their right", color: C.orange },
    { label: "Only if put in a blind trust", color: C.teal },
  ];
  const handleSelect = (i) => { setSelected(i); setTimeout(() => setShowResults(true), 400); };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: C.pulse, margin: 0 }}>Today's Question</p>
        <span style={{
          padding: "2px 8px", border: `1px solid ${C.border}`, borderRadius: 3,
          fontFamily: "'DM Sans', sans-serif", fontSize: 9, fontWeight: 600,
          letterSpacing: 1.5, textTransform: "uppercase", color: C.muted,
        }}>Example</span>
      </div>
      <p style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 20, color: C.bright, lineHeight: 1.5, marginBottom: 20 }}>
        Should members of Congress be banned from trading individual stocks?
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {options.map((opt, i) => (
          <button key={i} onClick={() => !showResults && handleSelect(i)} style={{
            padding: "10px 14px", fontFamily: "'DM Sans', sans-serif", fontSize: 13,
            color: selected === i && !showResults ? C.night : C.text, textAlign: "left",
            background: selected === i && !showResults ? C.accent : "transparent",
            border: `1px solid ${selected === i ? C.accent : C.border}`,
            borderRadius: 4, cursor: showResults ? "default" : "pointer",
            transition: "all 0.4s", position: "relative", overflow: "hidden",
          }}>
            {showResults && (
              <div style={{
                position: "absolute", left: 0, top: 0, bottom: 0,
                width: `${results[i]}%`, background: `${opt.color}33`,
                transition: "width 0.4s ease-out", borderRadius: 4,
              }} />
            )}
            <span style={{ position: "relative", zIndex: 1 }}>{opt.label}</span>
            {showResults && (
              <span style={{
                position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600,
                color: selected === i ? C.bright : C.muted, zIndex: 1,
              }}>{results[i]}%</span>
            )}
          </button>
        ))}
      </div>
      {showResults && (
        <>
          <FilterChips filter={filter} setFilter={setFilter} subFilter={subFilter} setSubFilter={setSubFilter} />
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.muted, marginTop: 10, textAlign: "center", fontStyle: "italic" }}>
            N = <span style={{ color: C.bright, fontWeight: 600, fontStyle: "normal" }}>{getCount(WEIGH_IN_COUNTS, filter, subFilter).toLocaleString()}</span> verified responses
          </p>
        </>
      )}
    </div>
  );
};

// ─── DEMO: TREND ───
const DemoTrend = () => {
  const canvasRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(-1);
  const [filter, setFilter] = useState("all");
  const [subFilter, setSubFilter] = useState(null);
  const months = ["'99", "'01", "'03", "'05", "'07", "'09", "'11", "'13", "'15", "'17", "'19", "'21", "'23", "'25"];
  const values = filter === "all" ? TREND_DEMO.all
    : (TREND_DEMO[filter] && TREND_DEMO[filter][subFilter]) || TREND_DEMO.all;
  const lineColor = filter === "all" ? C.pulse
    : (DEMO_COLORS[filter] && DEMO_COLORS[filter][subFilter]) || C.pulse;

  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    ctx.scale(2, 2);
    const cw = canvas.offsetWidth, ch = canvas.offsetHeight;
    ctx.clearRect(0, 0, cw, ch);
    const padL = 32, padR = 16, padT = 20, padB = 28;
    const gw = cw - padL - padR, gh = ch - padT - padB;
    const minV = 25, maxV = 85;

    ctx.strokeStyle = C.border + "66"; ctx.lineWidth = 0.5;
    for (let i = 0; i <= 4; i++) {
      const y = padT + (gh / 4) * i;
      ctx.beginPath(); ctx.moveTo(padL, y); ctx.lineTo(cw - padR, y); ctx.stroke();
    }
    ctx.fillStyle = C.muted; ctx.font = "9px 'DM Sans', sans-serif";
    ctx.textAlign = "right"; ctx.textBaseline = "middle";
    for (let i = 0; i <= 4; i++) {
      const v = maxV - ((maxV - minV) / 4) * i;
      ctx.fillText(`${Math.round(v)}%`, padL - 6, padT + (gh / 4) * i);
    }
    const pts = values.map((v, i) => ({
      x: padL + (gw / (values.length - 1)) * i,
      y: padT + gh - ((v - minV) / (maxV - minV)) * gh,
    }));
    const grad = ctx.createLinearGradient(0, padT, 0, padT + gh);
    grad.addColorStop(0, lineColor + "30"); grad.addColorStop(1, "transparent");
    ctx.fillStyle = grad; ctx.beginPath(); ctx.moveTo(pts[0].x, padT + gh);
    pts.forEach(p => ctx.lineTo(p.x, p.y));
    ctx.lineTo(pts[pts.length - 1].x, padT + gh); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = lineColor; ctx.lineWidth = 2; ctx.lineJoin = "round";
    ctx.beginPath(); pts.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)); ctx.stroke();
    pts.forEach((p, i) => {
      ctx.beginPath(); ctx.arc(p.x, p.y, i === hoverIdx ? 5 : 3, 0, Math.PI * 2);
      ctx.fillStyle = i === hoverIdx ? C.bright : lineColor; ctx.fill();
      if (i === hoverIdx) { ctx.strokeStyle = lineColor; ctx.lineWidth = 2; ctx.stroke(); }
    });
    ctx.fillStyle = C.muted; ctx.font = "9px 'DM Sans', sans-serif";
    ctx.textAlign = "center"; ctx.textBaseline = "top";
    pts.forEach((p, i) => { if (i % 3 === 0 || i === pts.length - 1) ctx.fillText(months[i], p.x, padT + gh + 8); });
    if (hoverIdx >= 0 && hoverIdx < pts.length) {
      const p = pts[hoverIdx];
      ctx.fillStyle = C.surface; ctx.strokeStyle = C.border; ctx.lineWidth = 1;
      const tw = 60, th = 24, tx = p.x - tw / 2, ty = p.y - th - 12;
      ctx.beginPath(); ctx.roundRect(tx, ty, tw, th, 4); ctx.fill(); ctx.stroke();
      ctx.fillStyle = C.bright; ctx.font = "bold 11px 'DM Sans', sans-serif";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText(`${months[hoverIdx]}: ${values[hoverIdx]}%`, p.x, ty + th / 2);
    }
  }, [hoverIdx, filter, subFilter, lineColor]);

  useEffect(() => { drawChart(); }, [drawChart]);
  const handleMouseMove = (e) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const padL = 32, padR = 16, gw = rect.width - padL - padR;
    const idx = Math.round(((x - padL) / gw) * (values.length - 1));
    setHoverIdx(Math.max(0, Math.min(values.length - 1, idx)));
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: C.pulse, margin: 0 }}>Real Data, Real Trends</p>
        <span style={{
          padding: "2px 8px", border: `1px solid ${C.accent}66`, borderRadius: 3,
          fontFamily: "'DM Sans', sans-serif", fontSize: 9, fontWeight: 600,
          letterSpacing: 1.5, textTransform: "uppercase", color: C.accent,
        }}>Source: Gallup</span>
      </div>
      <p style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 20, color: C.bright, lineHeight: 1.5, marginBottom: 16 }}>
        Will today's youth have a better life than their parents?
      </p>
      <canvas ref={canvasRef} onMouseMove={handleMouseMove} onMouseLeave={() => setHoverIdx(-1)}
        style={{ width: "100%", height: 200, borderRadius: 4, background: C.deep, display: "block", cursor: "crosshair" }} />
      <FilterChips filter={filter} setFilter={setFilter} subFilter={subFilter} setSubFilter={setSubFilter} />
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.muted, marginTop: 10, textAlign: "center", fontStyle: "italic" }}>
        N = <span style={{ color: C.bright, fontWeight: 600, fontStyle: "normal" }}>{getCount(TREND_COUNTS, filter, subFilter).toLocaleString()}</span> verified responses
      </p>
    </div>
  );
};

// ─── LATIN CAROUSEL ───
// Populi stays anchored; the left word cycles through civic Latin vocabulary.
const LATIN_PHRASES = [
  { latin: "VOX", en: "voice" },
  { latin: "VOLUNTAS", en: "will" },
  { latin: "SUFFRAGIUM", en: "vote" },
  { latin: "SENTENTIA", en: "judgment" },
  { latin: "CONSENSUS", en: "agreement" },
];

const LatinCarousel = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % LATIN_PHRASES.length), 3500);
    return () => clearInterval(t);
  }, []);
  const p = LATIN_PHRASES[idx];
  return (
    <section style={{ padding: "80px clamp(12px, 3vw, 24px)", textAlign: "center", maxWidth: 900, margin: "0 auto" }}>
      <div style={{
        display: "flex", alignItems: "baseline", gap: "clamp(10px, 3vw, 32px)",
        flexWrap: "nowrap", justifyContent: "center",
      }}>
        {/* Left: cycling Latin + English */}
        <div style={{ textAlign: "right", flex: "1 1 0", minWidth: 0 }}>
          <div key={idx} style={{ animation: "fadeIn 0.7s ease-out" }}>
            <div style={{
              fontFamily: "'Libre Baskerville', Georgia, serif",
              fontSize: "clamp(16px, 5vw, 48px)", fontWeight: 700,
              color: C.accent, letterSpacing: "clamp(0px, 0.3vw, 2px)", lineHeight: 1.1,
            }}>{p.latin}</div>
            <div style={{
              fontFamily: "'Libre Baskerville', Georgia, serif", fontStyle: "italic",
              fontSize: "clamp(11px, 1.5vw, 14px)", color: C.muted, marginTop: 6,
            }}>{p.en}</div>
          </div>
        </div>
        {/* Middle dot */}
        <div style={{
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontSize: "clamp(20px, 4vw, 44px)", color: C.border, lineHeight: 1, alignSelf: "center", flexShrink: 0,
        }}>·</div>
        {/* Right: anchored POPULI */}
        <div style={{ textAlign: "left", flex: "1 1 0", minWidth: 0 }}>
          <div style={{
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontSize: "clamp(16px, 5vw, 48px)", fontWeight: 700,
            color: C.bright, letterSpacing: "clamp(0px, 0.3vw, 2px)", lineHeight: 1.1,
          }}>POPULI</div>
          <div style={{
            fontFamily: "'Libre Baskerville', Georgia, serif", fontStyle: "italic",
            fontSize: "clamp(11px, 1.5vw, 14px)", color: C.muted, marginTop: 6,
          }}>of the people</div>
        </div>
      </div>
    </section>
  );
};

// ─── DEMO: MANDATE ───
const DemoMandate = () => {
  const [selected, setSelected] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [filter, setFilter] = useState("all");
  const [subFilter, setSubFilter] = useState(null);
  const results = filter === "all" ? MANDATE_DEMO.all
    : (MANDATE_DEMO[filter] && MANDATE_DEMO[filter][subFilter]) || MANDATE_DEMO.all;
  const options = [
    { label: "I support this", color: C.teal },
    { label: "Should cover all drugs, not just insulin", color: C.blue },
    { label: "Government shouldn't set drug prices", color: C.orange },
  ];
  const handleSelect = (i) => { setSelected(i); setTimeout(() => setShowResults(true), 400); };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, gap: 8, flexWrap: "wrap" }}>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: C.accent, margin: 0 }}>Just Decided</p>
        <span style={{
          padding: "2px 8px", border: `1px solid ${C.border}`, borderRadius: 3,
          fontFamily: "'DM Sans', sans-serif", fontSize: 9, fontWeight: 600,
          letterSpacing: 1.5, textTransform: "uppercase", color: C.muted,
        }}>Example</span>
      </div>
      <p style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 20, color: C.bright, lineHeight: 1.5, marginBottom: 20 }}>
        Congress just capped insulin prices at $35 for all Americans. Do you support this?
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {options.map((opt, i) => (
          <button key={i} onClick={() => !showResults && handleSelect(i)} style={{
            padding: "10px 14px", fontFamily: "'DM Sans', sans-serif", fontSize: 13,
            color: selected === i && !showResults ? C.night : C.text, textAlign: "left",
            background: selected === i && !showResults ? C.accent : "transparent",
            border: `1px solid ${selected === i ? C.accent : C.border}`,
            borderRadius: 4, cursor: showResults ? "default" : "pointer",
            transition: "all 0.4s", position: "relative", overflow: "hidden",
          }}>
            {showResults && (
              <div style={{
                position: "absolute", left: 0, top: 0, bottom: 0,
                width: `${results[i]}%`, background: `${opt.color}33`,
                transition: "width 0.4s ease-out", borderRadius: 4,
              }} />
            )}
            <span style={{ position: "relative", zIndex: 1 }}>{opt.label}</span>
            {showResults && (
              <span style={{
                position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)",
                fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600,
                color: selected === i ? C.bright : C.muted, zIndex: 1,
              }}>{results[i]}%</span>
            )}
          </button>
        ))}
      </div>
      {showResults && (
        <>
          <FilterChips filter={filter} setFilter={setFilter} subFilter={subFilter} setSubFilter={setSubFilter} />
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, color: C.muted, marginTop: 10, textAlign: "center", fontStyle: "italic" }}>
            N = <span style={{ color: C.bright, fontWeight: 600, fontStyle: "normal" }}>{getCount(MANDATE_COUNTS, filter, subFilter).toLocaleString()}</span> verified responses
          </p>
        </>
      )}
    </div>
  );
};

// ─── PHONE MOCKUP ───
// Interactive 4-screen walkthrough. Works on mobile and desktop via tap/click
// or keyboard arrows. Each screen manages its own demo state.
const PhoneMockup = () => {
  const [screen, setScreen] = useState(0);
  const [hasVoted, setHasVoted] = useState(false);
  const [voteValue, setVoteValue] = useState(null);

  const screens = [
    { title: "Today's Question",
      node: <HeroPoll
        onVote={(v) => { setVoteValue(v); setHasVoted(true); }}
        hasVoted={hasVoted} voteValue={voteValue} /> },
    { title: "Multiple Choice", node: <DemoWeighIn /> },
    { title: "Trend Over Time", node: <DemoTrend /> },
    { title: "Live Reaction", node: <DemoMandate /> },
  ];

  const next = () => setScreen((s) => (s + 1) % screens.length);
  const prev = () => setScreen((s) => (s - 1 + screens.length) % screens.length);

  // Keyboard arrow navigation for desktop users
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, []);

  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "0 16px" }}>
      <div style={{
        width: "100%", maxWidth: 360,
        background: "#050608",
        borderRadius: 44, padding: "14px 12px",
        border: `1px solid #222`,
        boxShadow: `0 24px 60px rgba(0,0,0,0.7), 0 0 0 2px #1a1a1a, inset 0 0 0 1px #222`,
        position: "relative",
      }}>
        {/* Side buttons (visual detail only) */}
        <div style={{ position: "absolute", left: -2, top: 120, width: 2, height: 40, background: "#222", borderRadius: "0 0 0 2px" }} />
        <div style={{ position: "absolute", left: -2, top: 180, width: 2, height: 40, background: "#222", borderRadius: "0 0 0 2px" }} />
        <div style={{ position: "absolute", right: -2, top: 150, width: 2, height: 64, background: "#222", borderRadius: "0 0 2px 0" }} />

        {/* Screen */}
        <div style={{
          background: C.night, borderRadius: 32, overflow: "hidden",
          height: 680, display: "flex", flexDirection: "column", position: "relative",
        }}>
          {/* Notch / Dynamic Island */}
          <div style={{
            position: "absolute", top: 10, left: "50%", transform: "translateX(-50%)",
            width: 100, height: 22, background: "#000", borderRadius: 14, zIndex: 3,
          }} />

          {/* Status bar */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "16px 24px 8px", fontSize: 10, color: C.muted,
            fontFamily: "'DM Sans', sans-serif", fontWeight: 500, letterSpacing: 0.5, position: "relative", zIndex: 1,
          }}>
            <span style={{ color: C.accent, fontStyle: "italic", fontFamily: "'Libre Baskerville', Georgia, serif" }}>prototype · example data</span>
            <span>{screen + 1}/{screens.length}</span>
          </div>

          {/* App header */}
          <div style={{
            padding: "14px 20px 10px", borderBottom: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <p style={{
              fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 14,
              color: C.bright, fontWeight: 700, letterSpacing: 2, margin: 0,
            }}>POPULI</p>
            <p style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: 9, fontWeight: 600,
              color: C.muted, letterSpacing: 1.5, textTransform: "uppercase", margin: 0,
            }}>{screens[screen].title}</p>
          </div>

          {/* Content — scrollable */}
          <div style={{
            flex: 1, overflowY: "auto", overflowX: "hidden",
            padding: "20px 18px 12px",
          }}>
            <div key={screen} style={{ animation: "fadeIn 0.4s ease-out" }}>
              {screens[screen].node}
            </div>
          </div>

          {/* Bottom nav bar */}
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            padding: "12px 18px 14px", borderTop: `1px solid ${C.border}`, gap: 8,
          }}>
            <button onClick={prev} style={{
              padding: "9px 14px", fontFamily: "'DM Sans', sans-serif", fontSize: 10,
              fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: C.muted,
              background: "transparent", border: `1px solid ${C.border}`, borderRadius: 3, cursor: "pointer",
            }}>← Prev</button>
            {/* Progress dots */}
            <div style={{ display: "flex", gap: 8 }}>
              {screens.map((_, i) => (
                <button key={i} onClick={() => setScreen(i)} aria-label={`Go to screen ${i + 1}`} style={{
                  width: 7, height: 7, borderRadius: "50%",
                  background: i === screen ? C.accent : C.border,
                  border: "none", padding: 0, cursor: "pointer", transition: "background 0.3s",
                }} />
              ))}
            </div>
            <button onClick={next} style={{
              padding: "9px 14px", fontFamily: "'DM Sans', sans-serif", fontSize: 10,
              fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase", color: C.accent,
              background: "transparent", border: `1px solid ${C.accent}`, borderRadius: 3, cursor: "pointer",
            }}>Next →</button>
          </div>

          {/* Home indicator */}
          <div style={{
            display: "flex", justifyContent: "center", padding: "6px 0 8px",
          }}>
            <div style={{ width: 110, height: 4, background: "#333", borderRadius: 2 }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── EMAIL CAPTURE ───
// ─── PROGRESSIVE SIGNUP: email → optional "get involved" form ───
const ROLE_OPTIONS = [
  "A concerned citizen",
  "Building something similar",
  "Journalist / writer",
  "Researcher / academic",
  "Investor or funder",
  "Working in civic tech",
  "Other",
];

const CONTRIBUTION_OPTIONS = [
  { key: "skills", label: "Contribute skills (design, engineering, writing, research, policy, legal, data, etc.)" },
  { key: "spread", label: "Help spread the word (via my platform, network, or community I run)" },
  { key: "fund", label: "Potential funding interest" },
  { key: "advise", label: "Advise on strategy or product" },
];

// Visually styled checkbox tile — replaces native checkbox
const CheckTile = ({ checked, onChange, children }) => (
  <label style={{
    display: "flex", alignItems: "flex-start", gap: 12,
    padding: "14px 16px", marginBottom: 10, cursor: "pointer",
    border: `1px solid ${checked ? C.accent : C.border}`,
    background: checked ? `${C.accent}12` : C.deep,
    borderRadius: 4, transition: "all 0.2s",
  }}>
    <input type="checkbox" checked={checked} onChange={onChange}
      style={{ position: "absolute", opacity: 0, pointerEvents: "none" }} />
    <span style={{
      display: "inline-block", width: 16, height: 16, flexShrink: 0, marginTop: 2,
      border: `1.5px solid ${checked ? C.accent : C.muted}`,
      background: checked ? C.accent : "transparent",
      borderRadius: 2, position: "relative",
    }}>
      {checked && (
        <span style={{
          position: "absolute", left: 4, top: 0, width: 5, height: 10,
          borderRight: `2px solid ${C.night}`, borderBottom: `2px solid ${C.night}`,
          transform: "rotate(45deg)",
        }} />
      )}
    </span>
    <span style={{
      fontFamily: "'DM Sans', sans-serif", fontSize: 14,
      color: C.text, lineHeight: 1.5,
    }}>{children}</span>
  </label>
);

const EmailCapture = () => {
  // stages: "idle" | "submitted" | "involving" | "done"
  const [stage, setStage] = useState("idle");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  // Stage 3 fields
  const [role, setRole] = useState("");
  const [contributions, setContributions] = useState([]);
  const [skillsDetail, setSkillsDetail] = useState("");
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState("");
  const [stage3Error, setStage3Error] = useState("");

  const postToSheet = async (payload) => {
    if (SHEET_URL === "REPLACE_WITH_YOUR_GOOGLE_APPS_SCRIPT_URL") return;
    await fetch(SHEET_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  };

  const handleStage1Submit = async () => {
    if (!email.includes("@") || submitting) return;
    setSubmitting(true);
    setError(false);
    try {
      await postToSheet({ email, timestamp: new Date().toISOString(), source: "landing-page" });
      setStage("submitted");
    } catch (e) {
      setError(true);
    }
    setSubmitting(false);
  };

  const toggleContribution = (key) => {
    setContributions((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleStage3Submit = async () => {
    if (submitting) return;
    if (!role) { setStage3Error("Please pick what brings you here."); return; }
    if (contributions.length === 0) { setStage3Error("Pick at least one way you'd like to contribute."); return; }
    setStage3Error("");
    setSubmitting(true);
    setError(false);
    try {
      await postToSheet({
        email,
        timestamp: new Date().toISOString(),
        source: "landing-page-involved",
        role,
        contributions: contributions.join(", "),
        skills_detail: contributions.includes("skills") ? skillsDetail : "",
        notes,
        location,
      });
      setStage("done");
    } catch (e) {
      setError(true);
    }
    setSubmitting(false);
  };

  // ═══ STAGE 4: final success after Get Involved form ═══
  if (stage === "done") {
    const goToBuild = (e) => {
      e.preventDefault();
      window.history.pushState({}, "", "/build");
      window.dispatchEvent(new PopStateEvent("popstate"));
    };
    return (
      <div style={{ textAlign: "center", padding: "20px 0", animation: "fadeIn 0.6s ease-out" }}>
        <p style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 22, color: C.accent, fontStyle: "italic" }}>
          We see you.
        </p>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: C.muted, marginTop: 12, lineHeight: 1.6 }}>
          When we're ready to activate, we'll reach out personally.
        </p>
        <div style={{ marginTop: 32, paddingTop: 24, borderTop: `1px solid ${C.border}` }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: C.muted, marginBottom: 14, lineHeight: 1.6 }}>
            Want to go deeper? Read the contributor brief &mdash; honest answers to the hard questions about verification, legal structure, and what we're building.
          </p>
          <a href="/build" onClick={goToBuild} style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: 12, fontWeight: 600,
            letterSpacing: 2, textTransform: "uppercase", color: C.accent,
            textDecoration: "none", borderBottom: `1px solid ${C.accent}44`, paddingBottom: 3,
          }}>Read the Contributor Brief →</a>
        </div>
      </div>
    );
  }

  // ═══ STAGE 3: "How do you want to help?" form ═══
  if (stage === "involving") {
    const inputStyle = {
      width: "100%", padding: "12px 14px",
      fontFamily: "'DM Sans', sans-serif", fontSize: 14,
      color: C.bright, background: C.deep, border: `1px solid ${C.border}`,
      borderRadius: 4, outline: "none", transition: "border-color 0.2s",
    };
    const labelStyle = {
      display: "block", fontFamily: "'DM Sans', sans-serif", fontSize: 12,
      fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase",
      color: C.muted, marginBottom: 12,
    };

    return (
      <div style={{ maxWidth: 560, margin: "0 auto", animation: "fadeIn 0.5s ease-out" }}>
        <p style={{
          fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 20,
          color: C.bright, textAlign: "center", marginBottom: 8,
        }}>How do you want to help?</p>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: C.muted,
          textAlign: "center", marginBottom: 32, lineHeight: 1.6,
        }}>
          A few quick questions so we know how to reach out when it's time.
        </p>

        {/* Q1: Role */}
        <div style={{ marginBottom: 28 }}>
          <label style={labelStyle}>What brings you here?</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
            <option value="">Pick one…</option>
            {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        {/* Q2: Contributions */}
        <div style={{ marginBottom: 28 }}>
          <label style={labelStyle}>How do you want to contribute? <span style={{ color: C.border, textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>(pick any)</span></label>
          {CONTRIBUTION_OPTIONS.map((opt) => (
            <CheckTile key={opt.key}
              checked={contributions.includes(opt.key)}
              onChange={() => toggleContribution(opt.key)}>
              {opt.label}
            </CheckTile>
          ))}
          {contributions.includes("skills") && (
            <div style={{ marginTop: 12, paddingLeft: 16, borderLeft: `2px solid ${C.accent}` }}>
              <label style={{ ...labelStyle, marginBottom: 8, fontSize: 11 }}>What kind of skills?</label>
              <input type="text" value={skillsDetail} onChange={(e) => setSkillsDetail(e.target.value)}
                placeholder="e.g. React engineer, brand design, policy research…"
                style={inputStyle} />
            </div>
          )}
        </div>

        {/* Q3: Notes */}
        <div style={{ marginBottom: 28 }}>
          <label style={labelStyle}>Tell us more <span style={{ color: C.border, textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>(optional)</span></label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="Anything you want us to know — a platform you run, a community you're part of, a specific way you'd help…"
            style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans', sans-serif" }} />
        </div>

        {/* Q4: Location */}
        <div style={{ marginBottom: 32 }}>
          <label style={labelStyle}>Location <span style={{ color: C.border, textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>(optional)</span></label>
          <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
            placeholder="City, State/Country"
            style={inputStyle} />
        </div>

        {stage3Error && (
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: C.red, textAlign: "center", marginBottom: 16 }}>
            {stage3Error}
          </p>
        )}
        {error && (
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: C.red, textAlign: "center", marginBottom: 16 }}>
            Something went wrong. Please try again.
          </p>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => setStage("submitted")} disabled={submitting} style={{
            padding: "14px 24px", fontFamily: "'DM Sans', sans-serif", fontSize: 13,
            fontWeight: 500, letterSpacing: 1.5, textTransform: "uppercase",
            color: C.muted, background: "transparent", border: `1px solid ${C.border}`,
            borderRadius: 4, cursor: "pointer", transition: "all 0.2s",
          }}>Back</button>
          <button onClick={handleStage3Submit} disabled={submitting} style={{
            padding: "14px 32px", fontFamily: "'DM Sans', sans-serif", fontSize: 13,
            fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase",
            color: C.night, background: submitting ? C.muted : C.accent, border: "none",
            borderRadius: 4, cursor: submitting ? "wait" : "pointer", transition: "background 0.3s",
          }}
            onMouseEnter={(e) => !submitting && (e.target.style.background = C.bright)}
            onMouseLeave={(e) => !submitting && (e.target.style.background = C.accent)}
          >{submitting ? "Sending…" : "Count me in"}</button>
        </div>
      </div>
    );
  }

  // ═══ STAGE 2: post-submit confirmation with secondary CTA ═══
  if (stage === "submitted") {
    return (
      <div style={{ textAlign: "center", padding: "20px 0", animation: "fadeIn 0.5s ease-out" }}>
        <p style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 18, color: C.accent }}>You've been heard.</p>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: C.muted, marginTop: 8 }}>
          We'll reach out when it's time to build this together.
        </p>

        <div style={{
          marginTop: 40, paddingTop: 32,
          borderTop: `1px solid ${C.border}`, maxWidth: 440, margin: "40px auto 0",
        }}>
          <p style={{
            fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 16,
            color: C.text, fontStyle: "italic", lineHeight: 1.6, marginBottom: 20,
          }}>
            Want to do more than stay in the loop?<br />
            Tell us how you'd like to help build this.
          </p>
          <button onClick={() => setStage("involving")} style={{
            padding: "12px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 12,
            fontWeight: 600, letterSpacing: 2, textTransform: "uppercase",
            color: C.accent, background: "transparent", border: `1px solid ${C.accent}`,
            borderRadius: 4, cursor: "pointer", transition: "all 0.2s",
          }}
            onMouseEnter={(e) => { e.target.style.background = `${C.accent}15`; }}
            onMouseLeave={(e) => { e.target.style.background = "transparent"; }}
          >Get Involved</button>
        </div>
      </div>
    );
  }

  // ═══ STAGE 1: email capture (unchanged behavior) ═══
  return (
    <div>
      <div style={{ display: "flex", gap: 0, maxWidth: 460, margin: "0 auto" }}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleStage1Submit()}
          placeholder="your@email.com"
          style={{
            flex: 1, padding: "14px 18px", fontFamily: "'DM Sans', sans-serif", fontSize: 14,
            color: C.bright, background: C.deep, border: `1px solid ${C.border}`,
            borderRight: "none", borderRadius: "4px 0 0 4px", outline: "none",
          }} />
        <button onClick={handleStage1Submit} disabled={submitting} style={{
          padding: "14px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 13,
          fontWeight: 600, letterSpacing: 1.5, textTransform: "uppercase",
          color: C.night, background: submitting ? C.muted : C.accent, border: "none",
          borderRadius: "0 4px 4px 0", cursor: submitting ? "wait" : "pointer", transition: "background 0.3s",
        }}
          onMouseEnter={(e) => !submitting && (e.target.style.background = C.bright)}
          onMouseLeave={(e) => !submitting && (e.target.style.background = C.accent)}
        >{submitting ? "..." : "Join"}</button>
      </div>
      {error && (
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: C.red, textAlign: "center", marginTop: 12 }}>
          Something went wrong. Please try again.
        </p>
      )}
      <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: C.border, textAlign: "center", marginTop: 12 }}>
        No spam. No selling your data. Obviously.
      </p>
    </div>
  );
};

// ─── MAIN PAGE ───
export default function PopuliLanding() {
  const [globeAge, setGlobeAge] = useState("all");

  const ageGroups = [
    { key: "all", label: "All" },
    { key: "a18", label: "18–29" },
    { key: "a30", label: "30–44" },
    { key: "a45", label: "45–59" },
    { key: "a60", label: "60+" },
  ];

  const goToManifesto = (e) => {
    e.preventDefault();
    window.history.pushState({}, '', '/vision');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const howItWorks = [
    { title: "One verified person. One voice.",
      body: "Every participant is confirmed to be a real, unique human. No bots. No duplicate accounts. No armies of fake identities." },
    { title: "Community-driven questions.",
      body: "The public has a say in what gets asked. Questions are submitted, elevated, and refined through transparent processes." },
    { title: "Living, explorable results.",
      body: "Results update continuously. Filter by region, age, or demographic. See where opinion holds and where it shifts." },
    { title: "Open methodology.",
      body: "Transparent rules, clear methodology, independent scrutiny. If it can't be audited, it doesn't belong in the infrastructure of democracy." },
  ];

  return (
    <div style={{ background: C.night, minHeight: "100vh", color: C.text }}>
      <link href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes pulseExpand { 0% { transform: scale(0.3); opacity: 0.6; } 100% { transform: scale(2.5); opacity: 0; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes typeIn { from { opacity: 0; letter-spacing: 12px; } to { opacity: 1; letter-spacing: 6px; } }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        ::selection { background: ${C.accent}44; color: ${C.bright}; }
      `}</style>

      {/* ═══ HERO ═══ */}
      <header style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        position: "relative", overflow: "hidden", padding: "0 24px",
      }}>
        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
          <PulseRing delay={0} size={200} />
          <PulseRing delay={2} size={300} />
          <PulseRing delay={4} size={400} />
        </div>
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: `linear-gradient(${C.border}15 1px, transparent 1px), linear-gradient(90deg, ${C.border}15 1px, transparent 1px)`,
          backgroundSize: "60px 60px", opacity: 0.4,
        }} />
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", maxWidth: 720 }}>
          {/* Kicker */}
          <p style={{
            display: "inline-block", padding: "6px 14px", marginBottom: 28,
            fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
            letterSpacing: 2, textTransform: "uppercase", color: C.accent,
            border: `1px solid ${C.accent}44`, borderRadius: 3,
            animation: "fadeIn 1.6s ease-out 0.2s both",
          }}>In development — join us</p>
          {/* Wordmark */}
          <h1 style={{
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontSize: "clamp(36px, 8vw, 72px)", fontWeight: 700,
            color: C.bright, letterSpacing: 4, marginBottom: 12,
            animation: "typeIn 1.5s ease-out forwards",
          }}>THE POPULI PROJECT</h1>
          {/* Tagline */}
          <p style={{
            fontFamily: "'DM Sans', sans-serif", fontSize: "clamp(11px, 2vw, 13px)",
            fontWeight: 500, letterSpacing: 4, textTransform: "uppercase",
            color: C.muted, marginBottom: 48, animation: "fadeIn 2s ease-out 0.5s both",
          }}>We the People. In real time.</p>
          {/* One-line pitch */}
          <p style={{
            fontFamily: "'Libre Baskerville', Georgia, serif",
            fontSize: "clamp(18px, 2.6vw, 24px)", color: C.text,
            lineHeight: 1.6, maxWidth: 640, margin: "0 auto",
            animation: "fadeInUp 1.5s ease-out 1s both",
          }}>
            Populi is a platform where verified people weigh in on the issues that matter &mdash; and the public sees <span style={{ color: C.accent }}>what the country actually thinks.</span>
          </p>
        </div>
        <div style={{
          position: "absolute", bottom: 40, left: "50%", transform: "translateX(-50%)",
          animation: "fadeIn 2s ease-out 2s both",
        }}>
          <div style={{ width: 1, height: 48, background: `linear-gradient(to bottom, transparent, ${C.muted})`, margin: "0 auto 8px" }} />
          <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 10, letterSpacing: 3, textTransform: "uppercase", color: C.muted }}>Scroll</span>
        </div>
      </header>

      {/* ═══ WHEN WERE YOU LAST COUNTED? (problem, compact) ═══ */}
      <Section>
        <h2 style={{
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontSize: "clamp(28px, 5vw, 40px)", color: C.bright,
          marginBottom: 32, lineHeight: 1.3, textAlign: "center",
        }}>When were you<br /><span style={{ color: C.accent }}>last counted?</span></h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 22, maxWidth: 620, margin: "0 auto" }}>
          <p style={{
            fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 16, lineHeight: 1.8,
            color: C.text, borderLeft: `2px solid ${C.border}`, paddingLeft: 24,
          }}>
            Not voted. Not surveyed. Counted &mdash; as in, someone actually wanted to know what you think about something that affects your life.
          </p>
          <p style={{
            fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 16, lineHeight: 1.8,
            color: C.text, borderLeft: `2px solid ${C.border}`, paddingLeft: 24,
          }}>
            Most of us can&rsquo;t remember. We vote when we can, and that&rsquo;s about it. One choice, every few years, that bundles a hundred opinions into a single lever. The rest of the time, nobody asks.
          </p>
          <p style={{
            fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 16, lineHeight: 1.8,
            color: C.text, borderLeft: `2px solid ${C.border}`, paddingLeft: 24,
          }}>
            Instead, we watch pundits explain what &ldquo;Americans believe&rdquo; &mdash; based on a few thousand phone calls, selected by methods nobody scrutinizes, treated as gospel. Every poll. Every headline. Every &ldquo;the public wants X.&rdquo;
          </p>
          <p style={{
            fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 18, lineHeight: 1.6,
            color: C.bright, borderLeft: `2px solid ${C.accent}`, paddingLeft: 24, fontStyle: "italic",
          }}>
            You think: nobody asked me. Nobody asked anyone I know.
          </p>
          <p style={{
            fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 18, lineHeight: 1.6,
            color: C.bright, borderLeft: `2px solid ${C.accent}`, paddingLeft: 24,
          }}>
            What would it look like if we actually asked?
          </p>
        </div>
      </Section>

      <Divider />

      {/* ═══ HOW IT WOULD WORK (compact) ═══ */}
      <Section>
        <h2 style={{
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontSize: "clamp(28px, 5vw, 40px)", color: C.bright,
          marginBottom: 48, lineHeight: 1.3, textAlign: "center",
        }}>How it would work.</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 32, maxWidth: 760, margin: "0 auto" }}>
          {howItWorks.map((item, i) => (
            <div key={i} style={{ paddingLeft: 20, borderLeft: `2px solid ${C.pulse}` }}>
              <p style={{
                fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
                color: C.pulse, marginBottom: 10,
              }}>{item.title}</p>
              <p style={{
                fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 14,
                lineHeight: 1.7, color: C.text,
              }}>{item.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Divider />

      {/* ═══ PHONE MOCKUP — what using Populi feels like ═══ */}
      <Section style={{ maxWidth: 900, padding: "80px 24px 60px" }}>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
          letterSpacing: 3, textTransform: "uppercase", color: C.pulse,
          textAlign: "center", marginBottom: 14,
        }}>What it would feel like</p>
        <h2 style={{
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontSize: "clamp(28px, 5vw, 40px)", color: C.bright,
          lineHeight: 1.3, textAlign: "center", marginBottom: 16,
        }}>A few questions.<br /><span style={{ color: C.accent }}>The world, counted.</span></h2>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: C.muted,
          textAlign: "center", maxWidth: 480, margin: "0 auto 48px", lineHeight: 1.6,
        }}>Tap through a few example questions. This is what the product could look like — every vote verified, every result public.</p>
        <PhoneMockup />
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: C.muted,
          textAlign: "center", maxWidth: 480, margin: "32px auto 0", lineHeight: 1.6,
          fontStyle: "italic",
        }}>The current prototype is intentionally conceptual. It&rsquo;s meant to convey the idea &mdash; the real product could be far better. That&rsquo;s part of what we&rsquo;re building together.</p>
      </Section>

      <Divider />

      {/* ═══ GLOBE — what the world looks like ═══ */}
      <Section style={{ maxWidth: 900 }}>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
          letterSpacing: 3, textTransform: "uppercase", color: C.pulse,
          textAlign: "center", marginBottom: 14,
        }}>What the world looks like</p>
        <h2 style={{
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontSize: "clamp(28px, 5vw, 40px)", color: C.bright,
          lineHeight: 1.3, textAlign: "center", marginBottom: 16,
        }}>The results,<br /><span style={{ color: C.pulse }}>mapped in real time.</span></h2>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: C.muted,
          textAlign: "center", maxWidth: 500, margin: "0 auto 48px", lineHeight: 1.6,
        }}>See what your city, your country, your world thinks. Explore by age. See where opinion is strong, where it&rsquo;s split, and where it&rsquo;s been manufactured.</p>

        {/* Globe card */}
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
          padding: 28,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: C.pulse, margin: 0 }}>
                  Global Pulse
                </p>
                <span style={{
                  padding: "2px 8px", border: `1px solid ${C.border}`, borderRadius: 3,
                  fontFamily: "'DM Sans', sans-serif", fontSize: 9, fontWeight: 600,
                  letterSpacing: 1.5, textTransform: "uppercase", color: C.muted,
                }}>Example</span>
              </div>
              <p style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 14, color: C.muted }}>
                Support for U.S. military action in Iran — by age group
              </p>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {ageGroups.map((g) => (
                <button key={g.key} onClick={() => setGlobeAge(g.key)} style={{
                  padding: "6px 12px", fontFamily: "'DM Sans', sans-serif", fontSize: 11,
                  fontWeight: globeAge === g.key ? 600 : 400,
                  color: globeAge === g.key ? C.night : C.muted,
                  background: globeAge === g.key ? C.pulse : "transparent",
                  border: `1px solid ${globeAge === g.key ? C.pulse : C.border}`,
                  borderRadius: 3, cursor: "pointer", transition: "all 0.3s",
                }}>{g.label}</button>
              ))}
            </div>
          </div>
          <Globe ageGroup={globeAge} />
          <GlobeVotePrompt />
        </div>
      </Section>

      <Divider />

      {/* ═══ MANIFESTO CTA ═══ */}
      <Section style={{ textAlign: "center" }}>
        <blockquote style={{
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontSize: "clamp(20px, 3.5vw, 28px)", fontStyle: "italic",
          color: C.bright, lineHeight: 1.6, maxWidth: 640, margin: "0 auto 24px",
        }}>&ldquo;What democracy is missing is not another platform for expression. It is a modern way to call the roll.&rdquo;</blockquote>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: C.muted,
          maxWidth: 460, margin: "0 auto 32px", lineHeight: 1.6,
        }}>The full argument — why this must exist, and what changes when everyone is finally heard.</p>
        <a href="/vision" onClick={goToManifesto} style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 600,
          letterSpacing: 1.5, textTransform: "uppercase", color: C.accent,
          textDecoration: "none", borderBottom: `1px solid ${C.accent}44`, paddingBottom: 4, cursor: "pointer",
        }}>Read the Manifesto →</a>
      </Section>

      <Divider />

      {/* ═══ EMAIL CAPTURE + GET INVOLVED ═══ */}
      <Section id="signup" style={{ padding: "100px 24px", textAlign: "center", background: `linear-gradient(to bottom, transparent, ${C.accentGlow})` }}>
        <h2 style={{
          fontFamily: "'Libre Baskerville', Georgia, serif",
          fontSize: "clamp(24px, 4vw, 36px)", color: C.bright, marginBottom: 16,
        }}>This only works at scale.</h2>
        <p style={{
          fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 16,
          color: C.muted, lineHeight: 1.7, maxWidth: 520, margin: "0 auto 40px",
        }}>A thousand voices are interesting. A million are undeniable. Every person who joins makes the data more representative. Add your voice early.</p>
        <EmailCapture />
      </Section>

      {/* ═══ LATIN CAROUSEL ═══ */}
      <LatinCarousel />

      {/* ═══ FOOTER ═══ */}
      <footer style={{ padding: "48px 24px", textAlign: "center", borderTop: `1px solid ${C.border}` }}>
        <p style={{ fontFamily: "'Libre Baskerville', Georgia, serif", fontSize: 16, color: C.muted, letterSpacing: 3 }}>THE POPULI PROJECT</p>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: C.border, marginTop: 8, letterSpacing: 2 }}>
          WE THE PEOPLE. IN REAL TIME.
        </p>
        <a href="/vision" onClick={goToManifesto} style={{
          display: "inline-block", marginTop: 20,
          fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600,
          letterSpacing: 1.5, textTransform: "uppercase", color: C.muted,
          textDecoration: "none", cursor: "pointer",
        }}>Read the Manifesto</a>
      </footer>
    </div>
  );
}
